import {
  DependencyList,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import { UseFormGetValues } from "react-hook-form";

import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  getFieldKey,
  getResourceType,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasEnumData } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import { EnumOption } from "../../core/types";
import { useInitialFieldData } from "../context/InitialFieldDataContext";
import { fetchFieldOptionsBatch } from "../services/fieldOptionsService";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import {
  fieldOptionsReducer,
  initialFieldOptionsState,
} from "./fieldOptionsReducer";

type EnumDataFieldParameter = FieldParameter;

export const useGetFieldOptions = (
  fieldParameter: EnumDataFieldParameter,
  _getValues: UseFormGetValues<Record<string, unknown>>,
  deps: DependencyList = []
) => {
  const auth = useAdvisorRequestAuth();
  const { optionsMap, loadingMap } = useInitialFieldData();
  const [state, dispatch] = useReducer(
    fieldOptionsReducer,
    initialFieldOptionsState
  );

  const fieldKey = getFieldKey(fieldParameter);
  const cachedOptions = fieldKey ? optionsMap[fieldKey] : undefined;
  const isCacheLoading = fieldKey ? loadingMap[fieldKey] : false;

  const shouldUseCache = useMemo(() => {
    if (!cachedOptions?.length || !hasEnumData(fieldParameter)) {
      return false;
    }

    const resourceType = getResourceType(fieldParameter);
    const hasDependencies =
      resourceType?.params && Object.keys(resourceType.params).length > 0;

    // Don't use cache if the field has dependencies, regardless of deps array length
    // This ensures that fields like assignee/advisor/service team refresh when form values change
    return !hasDependencies;
  }, [cachedOptions, fieldParameter]);

  const fetchOptionsData = useCallback(async (): Promise<EnumOption[]> => {
    // Use cache if available and appropriate
    if (shouldUseCache) {
      dispatch({ type: "SET_CACHED_OPTIONS", payload: cachedOptions! });
      return cachedOptions!;
    }

    dispatch({ type: "START_LOADING" });

    try {
      if (!auth || !hasEnumData(fieldParameter)) {
        dispatch({ type: "SET_OPTIONS", payload: [] });
        return [];
      }

      // Use our existing service but pass getValues for dependency resolution
      const batchResult = await fetchFieldOptionsBatch(
        [fieldParameter],
        auth,
        _getValues
      );
      const options = batchResult[fieldKey] || [];

      dispatch({ type: "SET_OPTIONS", payload: options });
      return options;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch options";
      dispatch({ type: "SET_ERROR", payload: errorMessage });
      console.error("Error fetching field options:", err);
      return [];
    }
  }, [
    auth,
    fieldParameter,
    fieldKey,
    shouldUseCache,
    cachedOptions,
    _getValues,
  ]);

  // Set cached options immediately when available
  useEffect(() => {
    if (shouldUseCache) {
      dispatch({ type: "SET_CACHED_OPTIONS", payload: cachedOptions! });
    }
  }, [shouldUseCache, cachedOptions]);

  useEffect(() => {
    fetchOptionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOptionsData, ...deps]);

  return {
    options: state.options,
    isLoading: state.isLoading || isCacheLoading,
    error: state.error,
    fetchOptionsData,
  };
};
